import React from 'react';
import { Box, Link, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { checkoutModes, type CheckoutMode } from 'skipify-types';

import { ConfigSection } from '../../../common';
import { MerchantAwareLink } from '../../../components/core/MerchantAwareLink';
import { useMerchant } from '../../../contexts/MerchantContext';
import { useGetMerchantQuery } from '../../../store/modules/merchant/api';
import { configurationPaths } from '../../../util/constants';
import { useEllaDraft } from '../EllaDraftContext';
import { merchantHasDefaultPaymentCredential } from '../merchantPayment';
import { isValidHandoffUrl, isValidOrderCompleteUrl } from '../../../util/helpers';
import { FORM_MAX_WIDTH, radioSx } from '../ellaFormStyles';

const CHECKOUT_MODE_COPY: Record<CheckoutMode, { title: string; description: string }> = {
  inline: {
    title: 'Inline',
    description:
      'Shoppers complete checkout in the agent conversation without leaving the chat experience.',
  },
  handoff: {
    title: 'Handoff',
    description:
      'Shoppers are sent to an external checkout experience. Optionally provide a handoff URL.',
  },
};

const isCheckoutMode = (value: unknown): value is CheckoutMode =>
  typeof value === 'string' && (checkoutModes as readonly string[]).includes(value);

export const CheckoutModeForm = (): JSX.Element => {
  const { merchantId, hasMerchantAccess } = useMerchant();
  const canOpenMerchantSettings = hasMerchantAccess();
  const { data: merchant } = useGetMerchantQuery(merchantId, { skip: !merchantId });
  const hasDefaultPaymentCredential = merchantHasDefaultPaymentCredential(merchant);
  const { draft, readOnly, updateDraft } = useEllaDraft();
  const mode = draft.checkoutMode.mode;
  const handoffUrl = draft.checkoutMode.handoffUrl ?? '';
  const trimmedHandoffUrl = handoffUrl.trim();
  const isHandoffUrlValid =
    mode !== 'handoff' || !trimmedHandoffUrl || isValidHandoffUrl(trimmedHandoffUrl);
  const orderCompleteUrl = draft.checkoutMode.orderCompleteUrl ?? '';
  const trimmedOrderCompleteUrl = orderCompleteUrl.trim();
  const isOrderCompleteUrlValid =
    mode !== 'inline' ||
    !trimmedOrderCompleteUrl ||
    isValidOrderCompleteUrl(trimmedOrderCompleteUrl);

  const setMode = (nextMode: CheckoutMode) => {
    if (readOnly) {
      return;
    }
    if (nextMode === 'inline' && !hasDefaultPaymentCredential) {
      return;
    }
    updateDraft((prev) => ({
      ...prev,
      checkoutMode:
        nextMode === 'handoff'
          ? {
              mode: nextMode,
              ...(prev.checkoutMode.handoffUrl !== undefined
                ? { handoffUrl: prev.checkoutMode.handoffUrl }
                : {}),
            }
          : {
              mode: nextMode,
              ...(prev.checkoutMode.orderCompleteUrl !== undefined
                ? { orderCompleteUrl: prev.checkoutMode.orderCompleteUrl }
                : {}),
            },
    }));
  };

  return (
    <Box
      component="form"
      id="ella-checkout-mode-form"
      data-testid="ella-checkout-mode-form"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      <ConfigSection
        title="Checkout mode *"
        description="Required. Choose how shoppers complete checkout with your agent."
        marginBottom={2}
      >
        <RadioGroup
          value={mode}
          onChange={(_e, value) => {
            if (isCheckoutMode(value)) {
              setMode(value);
            }
          }}
          sx={{ maxWidth: FORM_MAX_WIDTH, gap: 2 }}
        >
          {checkoutModes.map((option) => {
            const inlineBlocked = option === 'inline' && !hasDefaultPaymentCredential;
            const optionDisabled = readOnly || inlineBlocked;

            return (
              <Box key={option} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                  component="label"
                  onClick={() => {
                    if (!optionDisabled) {
                      setMode(option);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    cursor: optionDisabled ? 'not-allowed' : 'pointer',
                    // Mild fade: enough to read as disabled, still readable on white
                    opacity: inlineBlocked ? 0.72 : 1,
                  }}
                >
                  <Radio
                    size="small"
                    value={option}
                    checked={mode === option}
                    disabled={optionDisabled}
                    sx={{ ...radioSx, mt: '2px' }}
                    inputProps={
                      option === 'inline'
                        ? { 'aria-label': 'Inline checkout mode' }
                        : { 'aria-label': 'Handoff checkout mode' }
                    }
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography
                      sx={{
                        m: 0,
                        color: inlineBlocked ? 'text.secondary' : 'primary.dark',
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: 16,
                        fontWeight: 600,
                        lineHeight: 'normal',
                      }}
                    >
                      {CHECKOUT_MODE_COPY[option].title}
                      {inlineBlocked ? ' (unavailable)' : ''}
                    </Typography>
                    <Typography
                      variant="small"
                      color="text.secondary"
                      sx={{ m: 0, lineHeight: '20px' }}
                    >
                      {CHECKOUT_MODE_COPY[option].description}
                    </Typography>
                  </Box>
                </Box>

                {inlineBlocked && (
                  <Box
                    data-testid="ella-checkout-inline-requires-payment"
                    sx={{
                      ml: 4,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      maxWidth: FORM_MAX_WIDTH,
                    }}
                  >
                    <Typography
                      variant="small"
                      sx={{ m: 0, lineHeight: '20px', color: 'text.primary' }}
                    >
                      Inline checkout needs a payment gateway so shoppers can pay in chat. Set one
                      up in{' '}
                      {canOpenMerchantSettings ? (
                        <Link
                          component={MerchantAwareLink}
                          to={configurationPaths.PAYMENTS_AND_PROCESSING}
                          underline="hover"
                          data-testid="ella-checkout-payments-link"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ fontWeight: 600 }}
                        >
                          Settings → Payments &amp; Processing
                        </Link>
                      ) : (
                        <Box
                          component="span"
                          data-testid="ella-checkout-payments-link"
                          aria-disabled="true"
                          sx={{ fontWeight: 600, color: 'text.disabled', cursor: 'default' }}
                        >
                          Settings → Payments &amp; Processing
                        </Box>
                      )}
                      {canOpenMerchantSettings
                        ? ', then return here.'
                        : '. Ask someone with merchant settings access to add the gateway, then return here.'}
                    </Typography>
                  </Box>
                )}

                {option === 'handoff' && mode === 'handoff' && (
                  <TextField
                    fullWidth
                    label="Handoff URL (optional)"
                    placeholder="/checkout"
                    value={handoffUrl}
                    onChange={(e) =>
                      updateDraft((prev) => ({
                        ...prev,
                        checkoutMode: {
                          ...prev.checkoutMode,
                          mode: 'handoff',
                          handoffUrl: e.target.value,
                        },
                      }))
                    }
                    disabled={readOnly}
                    error={!isHandoffUrlValid}
                    helperText={
                      !isHandoffUrlValid
                        ? 'Enter a path starting with / (e.g. /checkout)'
                        : 'Optional. Use a path on your storefront (e.g. /checkout).'
                    }
                    inputProps={{ 'aria-label': 'Handoff URL (optional)' }}
                    data-testid="ella-checkout-handoff-url"
                    sx={{ maxWidth: FORM_MAX_WIDTH, ml: 4 }}
                  />
                )}

                {option === 'inline' && mode === 'inline' && (
                  <TextField
                    fullWidth
                    label="Order complete URL (optional)"
                    placeholder="/pages/thank-you"
                    value={orderCompleteUrl}
                    onChange={(e) =>
                      updateDraft((prev) => ({
                        ...prev,
                        checkoutMode: {
                          ...prev.checkoutMode,
                          mode: 'inline',
                          orderCompleteUrl: e.target.value,
                        },
                      }))
                    }
                    disabled={readOnly}
                    error={!isOrderCompleteUrlValid}
                    helperText={
                      !isOrderCompleteUrlValid
                        ? 'Enter a path starting with / or a full http(s) URL'
                        : 'Optional. Path (e.g. /pages/thank-you) or full URL. Shoppers are navigated here after the in-chat order complete page is displayed.'
                    }
                    inputProps={{ 'aria-label': 'Order complete URL (optional)' }}
                    data-testid="ella-checkout-order-complete-url"
                    sx={{ maxWidth: FORM_MAX_WIDTH, ml: 4 }}
                  />
                )}
              </Box>
            );
          })}
        </RadioGroup>
      </ConfigSection>
    </Box>
  );
};
